import {useMemo} from "react";
import {Card} from "../../../../../components/card/card.jsx";
import SentryEchart from "../../../../../components/echarts/sentry-echart.jsx";
import useGraphViewportWidth from "../../../../../hooks/use-graph-viewport-width.js";
import {classNameUtils} from "../../../../../utils/class-name-utils.js";
import {fluidPx} from "../../../../../utils/responsive-graph.js";

const customStyle = classNameUtils.cardVariants("customMenu");

const getStatusSummary = (summary) => {
  if (Array.isArray(summary)) return summary[0] ?? {};
  return summary ?? {};
};

const CautionedMaterialsStatusGraph = ({data}) => {
  const vw = useGraphViewportWidth();
  const summary = getStatusSummary(data?.cautionableMaterialsStatusSummary);

  const cautionOption = useMemo(() => {
    const totalCaution = Number(summary.total_caution ?? 0);
    const totalPermanent = Number(summary.total_permanent ?? 0);
    const totalGun = Math.min(totalCaution, Number(summary.total_gun ?? 0));
    const totalPermanentGun = Math.min(totalPermanent, Number(summary.total_permanent_gun ?? 0));
    const labels = ['Em cautela', 'Permanente'];
    const totals = [totalCaution, totalPermanent];
    const guns = [totalGun, totalPermanentGun];
    const remainingMaterials = totals.map((total, index) => total - guns[index]);
    const hasGuns = guns.some(gun => gun > 0);
    const labelFs = fluidPx(11, 16, vw);
    const categoryLabelFs = fluidPx(13, 20, vw);
    const displayValues = totals.map((total, index) => {
      const gun = guns[index];
      const remaining = remainingMaterials[index];

      if (!total || !gun) return [0, 100];
      if (!remaining) return [100, 0];

      const minimumGunWidth = 22;
      const minimumRemainingWidth = 42;
      const gunWidth = Math.min(
        100 - minimumRemainingWidth,
        Math.max(minimumGunWidth, gun / total * 100)
      );

      return [gunWidth, 100 - gunWidth];
    });
    const displayGuns = displayValues.map(([gun]) => gun);
    const displayRemainingMaterials = displayValues.map(([, remaining]) => remaining);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {type: 'none'},
        extraCssText: 'max-width: 320px; white-space: normal;',
        formatter: (params) => {
          const index = params[0]?.dataIndex ?? 0;

          const materialLabel = guns[index] > 0 ? 'Demais materiais' : 'Materiais';

          return labels[index]
            + '<br/>Total: <b>' + totals[index].toLocaleString('pt-BR') + '</b>'
            + (guns[index] > 0 ? '<br/>Armas: <b>' + guns[index].toLocaleString('pt-BR') + '</b>' : '')
            + '<br/>' + materialLabel + ': <b>' + remainingMaterials[index].toLocaleString('pt-BR') + '</b>';
        }
      },
      legend: {show: false},
      grid: {top: 34, left: 0, right: 0, bottom: 18},
      xAxis: {
        type: 'value',
        max: 100,
        show: false
      },
      yAxis: {
        type: 'category',
        data: labels,
        inverse: true,
        show: false
      },
      series: [
        {
          name: 'Armas',
          type: 'bar',
          stack: 'quantidade',
          barMaxWidth: 44,
          barCategoryGap: '38%',
          data: hasGuns ? displayGuns : [],
          itemStyle: {
            color: '#50A6F9',
            borderRadius: [8, 0, 0, 8]
          },
          label: {
            show: true,
            position: 'insideLeft',
            distance: 8,
            align: 'left',
            color: '#FFFFFF',
            fontSize: labelFs,
            fontWeight: 'bold',
            formatter: (params) => guns[params.dataIndex] > 0
              ? 'Armas: ' + guns[params.dataIndex].toLocaleString('pt-BR')
              : ''
          }
        },
        {
          name: hasGuns ? 'Demais materiais' : 'Materiais',
          type: 'bar',
          stack: 'quantidade',
          barMaxWidth: 44,
          barCategoryGap: '38%',
          data: displayRemainingMaterials.map((remaining, index) => ({
            value: remaining,
            itemStyle: {borderRadius: guns[index] > 0 ? [0, 8, 8, 0] : [8, 8, 8, 8]}
          })),
          itemStyle: {color: '#20609D'},
          label: {
            show: true,
            position: 'insideLeft',
            distance: 8,
            align: 'left',
            color: '#FFFFFF',
            fontSize: labelFs,
            fontWeight: 'bold',
            formatter: (params) => remainingMaterials[params.dataIndex] > 0
              ? (guns[params.dataIndex] > 0 ? 'Demais materiais: ' : 'Materiais: ') + remainingMaterials[params.dataIndex].toLocaleString('pt-BR')
              : ''
          }
        },
        {
          type: 'bar',
          barMaxWidth: 44,
          barCategoryGap: '38%',
          barGap: '-100%',
          data: totals.map(() => 100),
          silent: true,
          tooltip: {show: false},
          itemStyle: {color: 'transparent'},
          label: {
            show: true,
            position: [0, -6],
            align: 'left',
            verticalAlign: 'bottom',
            color: '#676a6c',
            fontSize: categoryLabelFs,
            fontWeight: 'bold',
            formatter: (params) => labels[params.dataIndex]
          }
        }
      ]
    };
  }, [summary, vw]);

  return(
    <Card className="col-span-2 h-full min-h-0 flex flex-col">
      <Card.Header className={customStyle.header}>
        <Card.Title className={customStyle.title}>Materiais em cautela</Card.Title>
      </Card.Header>
      <Card.Body className={customStyle.body}>
        <SentryEchart
          title="Distribuição"
          titleClass="text-[#676a6c]!"
          option={cautionOption}
        />
      </Card.Body>
    </Card>
  )
}

export default CautionedMaterialsStatusGraph;
